import { useState } from 'react';

import { cn } from '@utils/cn';

import {
  Accordion,
  Button,
  Chip,
  Icon,
  PaginationDots,
  Slider,
  Text,
  useSliderController
} from '@ui-component-library';

import { useOpenSubscriptionCheckout } from '@components/Checkout/config/SubscriptionCheckout/SubscriptionCheckoutContext';
import { DynamicModal } from '@components/DynamicModal';

import { plusFeatureHighlights, plusFeatures } from './constants';
import { ClickHandlers, FeatureHighlight, PlusBlockerDialogProps } from './PlusBlockerDialog.types';

const FeatureCard = ({ title, description, imageUrl }) => {
  return (
    <div className="flex flex-col">
      <img
        src={imageUrl ? `${Window.static_path}${imageUrl}` : 'https://placehold.co/520x240'}
        alt={title}
        className="h-[160px] w-full overflow-hidden rounded-xl object-cover lg:h-[220px]"
      />
      <div className="flex w-full flex-col py-2">
        <Text variant="text-base/leading-normal/medium" className="mb-1">
          {title}
        </Text>
        <Text variant="text-sm/leading-normal/normal">{description}</Text>
      </div>
    </div>
  );
};

export default function PlusBlockerDialog({
  isOpen,
  onClose,
  featureHighlight
}: PlusBlockerDialogProps) {
  const { openCheckout } = useOpenSubscriptionCheckout();
  const [sliderProps, slider] = useSliderController();

  const [selectedComparePackage] = useState('plus');

  const [openAccordionItem, setOpenAccordionItem] = useState<string | null>(null);

  const sortedPlusFeatureSlides = [...plusFeatureHighlights].sort(
    (a, b) => (featureHighlight === b.key ? 1 : 0) - (featureHighlight === a.key ? 1 : 0)
  );

  const handleNavigation = (target: string, isDemo?: boolean) => {
    if (isDemo) {
      return window.open(`/min-skog/demo/?view=${target}`, '_blank');
    }
    return window.open(target, '_blank');
  };

  const getDynamicCTAs = (): ClickHandlers => {
    const IS_DEMO = true;
    switch (featureHighlight as FeatureHighlight) {
      case 'property-value':
        return {
          secondaryCTA: () => handleNavigation('overblick', IS_DEMO),
          tertiaryCTA: () => handleNavigation('/fastighetsvardering/')
        };

      case 'property-data':
        return {
          secondaryCTA: () => handleNavigation('overblick', IS_DEMO),
          tertiaryCTA: () => handleNavigation('/skogsbruksplan/')
        };

      case 'section-details':
      case 'map-layers':
      case 'map-editing':
        return {
          secondaryCTA: () => handleNavigation('karta', IS_DEMO),
          tertiaryCTA: () => handleNavigation('/skogsbruksplan/')
        };

      case 'carbon-credits':
        return {
          secondaryCTA: undefined,
          tertiaryCTA: () => handleNavigation('/kolkrediter/')
        };

      case 'drone-analysis':
        return {
          secondaryCTA: undefined,
          tertiaryCTA: () => handleNavigation('/dronaranalys/')
        };

      case 'dfr':
        return {
          secondaryCTA: undefined,
          tertiaryCTA: () => handleNavigation('/skogsovervakning/')
        };

      case 'price-comparison':
        return {
          secondaryCTA: () => handleNavigation('virkespriser', IS_DEMO),
          tertiaryCTA: () => handleNavigation('/jamfor-virkespriser/')
        };

      case 'forest-advisory':
        return {
          secondaryCTA: undefined,
          tertiaryCTA: () => handleNavigation('/skoglig-radgivning/')
        };

      case 'forstand-file':
      default:
        return {};
    }
  };

  const { secondaryCTA, tertiaryCTA } = getDynamicCTAs();

  return (
    <DynamicModal
      isOpen={isOpen}
      onClose={onClose}
      bp={{ sm: 'drawer', lg: 'dialog' }}
      drawerSize="full"
      dialogSize="medium"
    >
      <DynamicModal.Header className="items-center justify-between">
        <div />
        <Text variant="text-base/leading-normal/medium" className="text-charcoal-500">
          Uppgradera till Treebula Plus
        </Text>
        <Button onClick={onClose} size="icon" variant="tertiary">
          <Icon name="CrossSimple" size="large" />
        </Button>
      </DynamicModal.Header>
      <DynamicModal.Content className="flex flex-col gap-4">
        <div className="flex flex-col items-start">
          <Chip
            label="Plus"
            className="mb-4 bg-[#F6F5FE] fill-[#6856C3] text-[#6856C3]"
            icon="SparkleFilled"
          />
          <Text variant="text-xl/leading-normal/medium" className="pb-1 text-[#1A1A1A]">
            Få full kontroll över din skog
          </Text>
          <Text variant="text-sm/leading-normal/normal" className="pb-2 text-[#737373]">
            Upptäck skador och förändringar, agera i tid.
          </Text>
          <Text variant="text-sm/leading-normal/normal" className="">
            <span className="text-[#1A1A1A]">Från 1 399 kr/år. </span>
            <span className="text-[#737373]"> Ingen bindningstid.</span>
          </Text>
        </div>

        <div className="flex flex-col">
          <Slider {...sliderProps} desktopVisible={1} mobileVisible={1}>
            {sortedPlusFeatureSlides.map((item) => (
              <div key={item.key} className="w-full">
                <FeatureCard
                  title={item.title}
                  description={item.description}
                  imageUrl={item.imageUrl}
                />
              </div>
            ))}
          </Slider>
          <div className="flex items-center justify-between px-4 pb-2">
            <PaginationDots
              total={plusFeatureHighlights.length}
              activeIndex={slider.currentSlide}
              onDotSelect={slider.goToSlide}
              className="cursor-pointer"
            />
            <div className="flex gap-3">
              <Button
                variant="primary"
                size="icon"
                onClick={slider.prev}
                disabled={slider.isFirstSlide}
                className="bg-[#0F0F0F] disabled:bg-[#828180]"
                aria-label="Föregående"
              >
                <Icon name="ArrowShortSimple" size="medium" className="fill-white" />
              </Button>
              <Button
                variant="primary"
                size="icon"
                onClick={slider.next}
                disabled={slider.isLastSlide}
                className="bg-[#0F0F0F] disabled:bg-[#828180]"
                aria-label="Nästa"
              >
                <Icon name="ArrowShortSimple" size="medium" className="rotate-180 fill-white" />
              </Button>
            </div>
          </div>
        </div>

        <table className="w-full table-fixed">
          <thead className="bg-white-50 table-header-group">
            <tr>
              <Text
                variant="text-xs/leading-normal/medium"
                as="th"
                className="w-auto min-w-0 px-2 py-2 text-left text-xs"
              >
                Det här får du
              </Text>
              <Text
                variant="text-xs/leading-normal/medium"
                as="th"
                className="w-14 shrink-0 p-4 text-center text-xs"
              >
                Bas
              </Text>
              <Text
                variant="text-xs/leading-normal/medium"
                as="th"
                className="bg-purple-plum-50 w-14 shrink-0 rounded-t-xl p-4 text-center text-[12px]"
              >
                Plus
              </Text>
            </tr>
          </thead>

          <tbody>
            {plusFeatures.map((comparePackage) => (
              <tr
                key={comparePackage.key}
                className="border-charcoal-500/16 cursor-pointer border-t-[0.5px] border-solid"
                onClick={() =>
                  setOpenAccordionItem(
                    openAccordionItem === comparePackage.key ? null : comparePackage.key
                  )
                }
              >
                <td className="py-3 pr-2 lg:py-4">
                  <Accordion
                    underline={false}
                    iconPosition="left"
                    isOpen={openAccordionItem === comparePackage.key}
                    onOpenChange={(open) => setOpenAccordionItem(open ? comparePackage.key : null)}
                  >
                    <Accordion.Header>
                      <Text variant="text-xs/leading-normal/normal" className="text-charcoal-500">
                        {comparePackage.title}
                      </Text>
                    </Accordion.Header>
                    <Accordion.Content>
                      <Text
                        variant="text-xs/leading-normal/normal"
                        className="block text-[#1A1A1A] lg:hidden"
                      >
                        {comparePackage.description}
                      </Text>
                      <Text
                        variant="text-xs/leading-normal/normal"
                        className="hidden text-[#171717] lg:block"
                      >
                        {comparePackage.description}
                      </Text>
                    </Accordion.Content>
                  </Accordion>
                </td>

                {['bas', selectedComparePackage].map((key) => {
                  const isIncluded = comparePackage.includedIn.includes(key);
                  return (
                    <td
                      key={key}
                      className={`shrink-0 ${key !== 'bas' ? 'bg-purple-plum-50' : ''}`}
                    >
                      <Icon
                        size="medium"
                        name={isIncluded ? 'CheckSimple' : 'Minus'}
                        className={cn(isIncluded ? 'fill-[#1A1A1A]' : 'fill-[#737373]')}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </DynamicModal.Content>
      <DynamicModal.Footer className="gap-3 md:flex-col">
        {secondaryCTA && (
          <Button variant="secondary" onClick={secondaryCTA}>
            Prova i exempelfastighet
          </Button>
        )}

        <Button
          variant="primary"
          onClick={() => {
            onClose();
            openCheckout();
          }}
        >
          Bli pluskund
        </Button>

        {tertiaryCTA && (
          <Button variant="tertiary" onClick={tertiaryCTA}>
            Läs mer
          </Button>
        )}
      </DynamicModal.Footer>
    </DynamicModal>
  );
}
